import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials, siteInfo } from '@/mocks/comics';

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('website_alt') as string;
    if (honeypot?.trim()) {
      setFormState('success');
      return;
    }
    try {
      const response = await fetch('https://readdy.ai/api/form/d9pbppaftmtnfcfh0k4g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let serverMsg = '';
      try {
        const parsed = JSON.parse(responseText);
        serverMsg = parsed?.meta?.message || parsed?.message || '';
      } catch { /* ignore */ }
      if (response.ok) {
        setFormState('success');
      } else {
        setFormState('error');
        setFormError(serverMsg || '提交失败，请重试');
      }
    } catch {
      setFormState('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "关于我们 - 韩国漫画网",
    "description": "国内专业的韩国漫画在线阅读平台",
    "url": "https://www.koreanmanhwa.com/about",
    "isPartOf": {
      "@type": "WebSite",
      "name": "韩国漫画网",
      "url": "https://www.koreanmanhwa.com"
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.koreanmanhwa.com/" },
      { "@type": "ListItem", "position": 2, "name": "关于我们" }
    ]
  };

  return (
    <>
      <title>关于我们 - 品牌介绍_口碑评价_联系方式 - 韩国漫画网</title>
      <meta name="description" content="韩国漫画网是国内专业的韩国漫画在线阅读平台。了解我们的品牌故事、读者口碑评价，以及联系方式。提供海量好看韩国漫画，支持韩漫在线无删减下拉式免费阅读。" />
      <meta name="keywords" content="韩国漫画网,关于我们,品牌介绍,韩漫免费阅读,联系方式" />
      <link rel="canonical" href="https://www.koreanmanhwa.com/about" />
      <meta property="og:title" content="关于我们 - 韩国漫画网" />
      <meta property="og:description" content="国内专业的韩国漫画在线阅读平台，了解我们的品牌故事、读者口碑评价。" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://www.koreanmanhwa.com/about" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content="关于我们 - 韩国漫画网" />
      <meta name="twitter:description" content="国内专业的韩国漫画在线阅读平台。" />
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <ScrollToTop />
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <main className="flex-1 pt-20 pb-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

            <section className="mt-6">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center">
                关于韩国漫画网
              </h1>
              <p className="mt-4 text-sm md:text-base text-foreground-600 text-center max-w-3xl mx-auto leading-relaxed">
                韩国漫画网创立于2018年，是国内最早一批专注于韩国漫画（韩漫）在线免费阅读的平台之一。
                八年来，我们始终坚持以读者为中心，致力于为广大韩漫爱好者提供海量、优质、无删减的韩国漫画资源。
              </p>
            </section>

            <section className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { icon: 'ri-heart-pulse-line', title: '我们的使命', desc: '打破语言和地域的界限，让每一位中文读者都能畅享原汁原味的韩国漫画。我们坚信好的故事值得被更多人看到。', color: 'primary' },
                { icon: 'ri-eye-line', title: '我们的愿景', desc: '成为全球最受信赖的中文韩漫阅读平台，构建一个充满活力的漫画文化交流社区。', color: 'accent' },
                { icon: 'ri-shield-star-line', title: '核心价值', desc: '坚持免费、无删减、高品质三大原则。尊重创作者，保护读者体验，承诺永不投放干扰性广告。', color: 'secondary' },
              ].map((item) => {
                const colorIcon = item.color === 'primary' ? 'text-primary-600 bg-primary-50' : item.color === 'accent' ? 'text-accent-600 bg-accent-50' : 'text-secondary-600 bg-secondary-50';
                return (
                  <div key={item.title} className="bg-white rounded-lg border border-background-200/70 p-6 text-center">
                    <div className={`w-14 h-14 rounded-full ${colorIcon} flex items-center justify-center mx-auto`}>
                      <i className={`${item.icon} text-xl w-5 h-5 flex items-center justify-center`}></i>
                    </div>
                    <h2 className="mt-4 text-base font-heading font-bold text-foreground-950">{item.title}</h2>
                    <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                );
              })}
            </section>

            <section className="mt-14">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-8">
                发展历程
              </h2>
              <div className="relative max-w-2xl mx-auto">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200 md:-translate-x-px"></div>
                {[
                  { year: '2018', title: '平台创立', desc: '韩国漫画网正式上线，首批收录2000+部韩国漫画资源。' },
                  { year: '2019', title: '快速成长', desc: '日活用户突破50万，漫画库扩充至15000+部，覆盖全部分类。' },
                  { year: '2020', title: '体验升级', desc: '全面升级下拉式阅读引擎，支持高清画质，适配全终端设备。' },
                  { year: '2022', title: '百万社区', desc: '注册用户突破500万，成为国内最大的韩漫阅读平台之一。' },
                  { year: '2024', title: '内容深耕', desc: '与韩国多家顶级漫画平台建立合作，实现漫画同步首发更新。' },
                  { year: '2026', title: '新的征程', desc: '持续创新，AI智能推荐系统上线，让每位读者都能发现心仪的好看韩国漫画。' },
                ].map((milestone, idx) => (
                  <div key={idx} className={`relative flex items-start gap-4 md:gap-8 mb-8 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-500 text-white flex items-center justify-center text-xs font-bold z-10">
                      {idx + 1}
                    </div>
                    <div className={`flex-1 bg-white rounded-lg border border-background-200/70 p-5 ${idx % 2 === 0 ? 'md:mr-8' : 'md:ml-8'}`}>
                      <span className="text-xs font-bold text-accent-600 whitespace-nowrap">{milestone.year}</span>
                      <h3 className="text-sm font-semibold text-foreground-900 mt-1">{milestone.title}</h3>
                      <p className="text-xs text-foreground-600 mt-2 leading-relaxed">{milestone.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-14">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-8">
                读者评价
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {testimonials.map((t) => (
                  <div key={t.name} className="bg-white rounded-lg border border-background-200/70 p-6">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">&ldquo;{t.comment}&rdquo;</p>
                    <div className="flex items-center gap-3 mt-4 pt-4 border-t border-background-100">
                      <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" loading="lazy" />
                      <span className="text-sm font-semibold text-foreground-900">{t.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-14">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-8">
                联系我们
              </h2>
              <div className="max-w-2xl mx-auto grid grid-cols-1 md:grid-cols-5 gap-8">
                <div className="md:col-span-3 bg-white rounded-lg border border-background-200/70 p-6">
                  {formState === 'success' ? (
                    <div className="text-center py-10">
                      <i className="ri-checkbox-circle-line text-5xl text-accent-500 w-12 h-12 flex items-center justify-center mx-auto"></i>
                      <p className="mt-4 text-lg font-semibold text-foreground-900">提交成功！</p>
                      <p className="text-sm text-foreground-500 mt-2">感谢您的留言，我们会尽快回复您。</p>
                      <button
                        onClick={() => setFormState('idle')}
                        className="mt-4 text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
                      >
                        继续留言
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} data-readdy-form>
                      <div className="space-y-4">
                        <div>
                          <label htmlFor="contact_name" className="block text-sm font-medium text-foreground-700 mb-1.5">您的姓名</label>
                          <input type="text" id="contact_name" name="name" required className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors" placeholder="请输入您的姓名" />
                        </div>
                        <div>
                          <label htmlFor="contact_email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱地址</label>
                          <input type="email" id="contact_email" name="email" required className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors" placeholder="请输入您的邮箱" />
                        </div>
                        <div>
                          <label htmlFor="contact_subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                          <input type="text" id="contact_subject" name="subject" required className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors" placeholder="请输入留言主题" />
                        </div>
                        <div>
                          <label htmlFor="contact_message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                          <textarea id="contact_message" name="message" rows={4} maxLength={500} required className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors resize-none" placeholder="请输入您的留言内容（500字以内）"></textarea>
                          <p className="text-xs text-foreground-400 mt-1 text-right">最多500字符</p>
                        </div>
                        <div className="anti-spam-field">
                          <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                        </div>
                        <button type="submit" className="w-full py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                          发送留言
                        </button>
                      </div>
                      {formState === 'error' && (
                        <p className="mt-3 text-sm text-red-600">{formError || '提交失败，请重试'}</p>
                      )}
                    </form>
                  )}
                </div>
                <div className="md:col-span-2 space-y-5">
                  <div className="bg-white rounded-lg border border-background-200/70 p-5">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-4">联系方式</h3>
                    <div className="space-y-3">
                      {[
                        { icon: 'ri-mail-line', label: '邮箱', value: siteInfo.contact.email },
                        { icon: 'ri-wechat-line', label: '微信', value: siteInfo.contact.wechat },
                        { icon: 'ri-qq-line', label: 'QQ', value: siteInfo.contact.qq },
                        { icon: 'ri-weibo-line', label: '微博', value: siteInfo.contact.weibo },
                      ].map((item) => (
                        <div key={item.label} className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-secondary-100 text-secondary-600 flex items-center justify-center flex-shrink-0">
                            <i className={`${item.icon} text-sm w-4 h-4 flex items-center justify-center`}></i>
                          </div>
                          <div>
                            <p className="text-xs text-foreground-500">{item.label}</p>
                            <p className="text-sm font-medium text-foreground-800">{item.value}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-white rounded-lg border border-background-200/70 p-5">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-3">工作时间</h3>
                    <p className="text-sm text-foreground-600">周一至周五：9:00 - 18:00</p>
                    <p className="text-sm text-foreground-600">周末及节假日：10:00 - 17:00</p>
                    <p className="text-xs text-foreground-400 mt-2">我们会在24小时内回复您的留言</p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}